(function () {
  'use strict';

  const GWEB_BASE = 'https://kinggoldretail.e-jewelry-softwarehouse.com';
  const GOLD_RATE_URL = `${GWEB_BASE}/Sup/api/gold-rate/`;
  const GOLD_POLL_MS = 30 * 1000;
  const CURRENCY = 'EGP';
  const PROXY_PATH = 'https://kinggold-dashboard-next.vercel.app/api/shopify';

  let globalGold = null;
  const goldListeners = [];
  let goldTimer = null;
  let goldFetchController = null;
  let goldFetchPromise = null;

  function computePrice({ pr18, usdRate, weight, prc, prcus }) {
    if (!Number.isFinite(pr18) || pr18 <= 0) return null;
    if (!Number.isFinite(weight) || weight <= 0) return null;
    const rate = Number.isFinite(usdRate) && usdRate > 0 ? usdRate : 1;
    const p = Number.isFinite(prc) ? prc : 0;
    const pu = Number.isFinite(prcus) ? prcus : 0;
    let total;
    if (pu > 0) {
      const totus = (pu + pr18 / rate) * weight;
      total = totus * rate;
    } else if (p > 0) {
      total = (p + pr18) * weight;
    } else {
      total = pr18 * weight;
    }
    return Math.sign(total) * Math.floor(Math.abs(total) + 0.5);
  }

  function roundToNearest5(n) {
    if (!Number.isFinite(n)) return null;
    return Math.round(n / 5) * 5;
  }

  function parseWeight(s) {
    if (s == null) return null;
    const m = String(s).trim().match(/^([\d.]+)/);
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  }

  function parseNum(s) {
    if (s == null || s === '') return 0;
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  }

  function formatPrice(amount) {
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: CURRENCY,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${Math.round(amount).toLocaleString()} ${CURRENCY}`;
    }
  }

  function notifyGold() {
    for (const fn of goldListeners) {
      try { fn(globalGold); } catch (e) { /* swallow listener errors */ }
    }
  }

  async function fetchGlobalGold() {
    if (goldFetchController) goldFetchController.abort();
    const controller = new AbortController();
    goldFetchController = controller;
    const promise = (async () => {
      try {
        const res = await fetch(GOLD_RATE_URL, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        globalGold = {
          pr18: Number(data.pr18),
          pr21: Number(data.pr21),
          pr24: Number(data.pr24),
          dollar: Number(data.dollar),
          updated_at: data.updated_at,
        };
        notifyGold();
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }
    })();
    goldFetchPromise = promise;
    try { await promise; } finally {
      if (goldFetchPromise === promise) goldFetchPromise = null;
    }
  }

  function startGlobalGoldPolling() {
    if (goldTimer) return;
    fetchGlobalGold();
    goldTimer = setInterval(fetchGlobalGold, GOLD_POLL_MS);
  }

  function stopGlobalGoldPolling() {
    if (goldTimer) {
      clearInterval(goldTimer);
      goldTimer = null;
    }
  }

  function onGoldChange(fn) {
    goldListeners.push(fn);
    if (globalGold) {
      try { fn(globalGold); } catch (e) { /* swallow */ }
    }
    if (goldListeners.length === 1) startGlobalGoldPolling();
    return function unsubscribe() {
      const i = goldListeners.indexOf(fn);
      if (i >= 0) goldListeners.splice(i, 1);
      if (goldListeners.length === 0) stopGlobalGoldPolling();
    };
  }

  function getGold() {
    return globalGold;
  }

  async function refreshGold() {
    await fetchGlobalGold();
    return globalGold;
  }

  async function syncVariantPrice(sku) {
    if (!sku) return { ok: false, reason: 'no-sku' };
    const url = `${PROXY_PATH}/refresh-price?sku=${encodeURIComponent(sku)}`;
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) return { ok: false, reason: `http-${res.status}` };
      const data = await res.json();
      // `updated: true` is the only signal that Shopify's stored price was
      // actually confirmed written — the server may return found:true with
      // a computed price it never managed to PUT (throttled / rate-limited
      // upstream). Trusting `found` alone let a customer proceed to
      // checkout on a stale price with no error at all.
      if (!data || !data.found) return { ok: false, reason: 'not-found', data };
      if (!data.updated) return { ok: false, reason: 'not-confirmed', data };
      return { ok: true, data };
    } catch (e) {
      return { ok: false, reason: 'network', error: e && e.message };
    }
  }

  window.KgPricing = {
    computePrice,
    roundToNearest5,
    parseWeight,
    parseNum,
    formatPrice,
    getGold,
    refreshGold,
    onGoldChange,
    syncVariantPrice,
    constants: { GWEB_BASE, GOLD_RATE_URL, GOLD_POLL_MS, CURRENCY, PROXY_PATH },
  };
})();
